import { useState, useRef, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import useFormatting from '../../hooks/useFormatting'
import DisplayElement from '../display/DisplayElement'
import { TableDecorator } from '../table/Table'
import { FormGroup } from '../form/FormComponents'
import Select from '../select/Select'
import NumericInput from '../numeric/NumericInput'
import DateInput from '../calendar/DateInput'
import Connect from '../select/Connect'
import HConnect from '../select/HConnect'
import { Icon_Search } from '../../styles/icons'

const CellWrapper = styled.div`
    box-sizing: border-box;
    flex: 0 1 ${props => props.$len || 'auto'};
    flex-shrink: 1;
    min-width: 0px;
    display: flex;
    padding: 0;
    align-items: stretch;
    justify-content: flex-start;
    overflow-wrap: break-word;
    word-break: break-word;
    border-right: 1px solid ${({ theme }) => theme.table.border};
    background-color: ${({ $editable, $focused, $editing, theme }) =>
        $editing ? 'transparent' : ($focused && $editable ? theme.table.hover : ($editable ? 'transparent' : theme.form.readOnlyBg))};

    &:last-child {
        border-right: none;
    }

    /* The cell's own text only — not the rows of a picker opened inside it,
       which carry their own weight (bold for headings). */
    > div > p {
        margin: 0;
        font-size: var(--font-XS);
        line-height: 24px;
        display: block;
        color: ${({ theme }) => theme.font.color};
        font-weight: 500;
        user-select: auto;
        white-space: normal;
        max-width: 100%;
    }
`
const ClickableDisplay = styled.div`
    cursor: ${({ $editable }) => $editable ? 'pointer' : 'default'};
    flex: 1;
    display: flex;
    align-items: center;
    padding: 2px 8px;
    border: 1px solid transparent;
    border-bottom: ${({ $editable, theme }) => $editable ? `1px dashed ${theme.table.border}` : '1px solid transparent'};
    border-radius: 0px;
    background-color: ${({ $hovered, $editable, theme }) => $hovered && $editable ? theme.table.active : 'transparent'};
    transition: background-color ${({ $hovered }) => $hovered ? '0s' : '0.8s'} ease;
`

const EditInput = styled.div`
    flex: 1;
    display: flex;
    align-items: stretch;
    margin: 0px;
    padding: 0;
    input, select {
        width: 100%;
        height: 100%;
        font-size: var(--font-XS);
        padding: 0.375rem 0.5rem;
        border: 1px solid ${({ theme }) => theme.form.border};
        border-radius: 0px;
        background: ${({ theme }) => theme.form.textControl.background};
        color: ${({ theme }) => theme.form.font};
        line-height: 1.5;
        box-sizing: border-box;
        transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
        box-shadow: ${({ theme }) => theme.form.textControl.boxShadow};

        &:focus {
            outline: 0;
            border: ${({ theme }) => theme.form.textControl.activeBorder};
            box-shadow: ${({ theme }) => theme.form.textControl.activeBoxShadow};
        }
    }
`

const GridListCell = ({ column, row, rowIdx, colIdx, editable, onUpdate, useStore, entity, isFocused, browsing, onNavigate, onCellClick, onCellEditingChange }) => {
    const { t } = useTranslation()
    const { formatString } = useFormatting()
    const [editing, setEditing] = useState(false)
    const [editValue, setEditValue] = useState(null)
    const inputRef = useRef(null)
    const wrapperRef = useRef(null)
    const autoOpenRef = useRef(false)

    const isEditable = editable !== undefined ? editable : column.editable || false
    const fieldValue = row[column.id]
    const displayField = column.displayField ? row[column.displayField] : null
    const [hoverActive, setHoverActive] = useState(false)
    const hoverTimerRef = useRef(null)

    useEffect(() => () => clearTimeout(hoverTimerRef.current), [])

    const handleMouseEnter = useCallback(() => {
        if (!isEditable) return
        setHoverActive(true)
        clearTimeout(hoverTimerRef.current)
        hoverTimerRef.current = setTimeout(() => setHoverActive(false), 2000)
    }, [isEditable])

    const handleMouseLeave = useCallback(() => {
        clearTimeout(hoverTimerRef.current)
        setHoverActive(false)
    }, [])

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus?.()
        }
    }, [editing])

    // Arrived by roaming (plain arrows), the cell takes focus but stays closed.
    useEffect(() => {
        if (isFocused && isEditable && !editing && wrapperRef.current) {
            if (browsing && document.activeElement !== wrapperRef.current) skipAutoEditRef.current = true
            wrapperRef.current.focus()
        }
    }, [isFocused])

    /*
     * Closing the editor removes the input that had focus. Unless focus went
     * somewhere on purpose (the next cell, a click elsewhere), it falls to the
     * page and the keyboard reaches no cell — picking the last column's VAT
     * left Ctrl+Shift+↓ with nothing to act on. It comes back to the cell,
     * without reopening the editor it just left.
     */
    const wasEditingRef = useRef(false)
    const skipAutoEditRef = useRef(false)
    useEffect(() => {
        if (wasEditingRef.current && !editing) {
            requestAnimationFrame(() => {
                const active = document.activeElement
                if (wrapperRef.current && (!active || active === document.body)) {
                    skipAutoEditRef.current = true
                    wrapperRef.current.focus()
                }
            })
        }
        wasEditingRef.current = editing
    }, [editing])

    // An emptied number leaves the record (undefined), so the schema's default
    // applies — '' is not a number and fails validation.
    const emptyNumber = (val) => val === '' || val === null || val === undefined || Number.isNaN(val)

    const handleNumericSetValue = useCallback(({ target: { value: val } }) => {
        setEditValue(val)
        if (onUpdate) onUpdate(rowIdx, column.id, emptyNumber(val) ? undefined : val)
    }, [rowIdx, column.id, onUpdate])

    const startEdit = useCallback(() => {
        setEditing(true)
        setEditValue(fieldValue ?? '')
    }, [fieldValue])

    const handleClick = () => {
        if (!isEditable) return
        autoOpenRef.current = false
        onCellClick?.(rowIdx, colIdx)
        startEdit()
    }

    const handleFocus = () => {
        if (skipAutoEditRef.current) {
            skipAutoEditRef.current = false
            return
        }
        // An inline picker is typed into, so it opens on arrival like a text
        // cell; a modal one waits for Enter or a click.
        const autoEdit = (column.type !== 'connect' && column.type !== 'hconnect') || column.inline
        if (isEditable && !editing && autoEdit) {
            onCellClick?.(rowIdx, colIdx)
            startEdit()
        }
    }

    const handleCellBlur = useCallback((e) => {
        if (!wrapperRef.current?.contains(e.relatedTarget)) {
            if (editing && (column.type === 'connect' || column.type === 'hconnect' || column.type === 'select')) {
                if (e.target?.value === '' && fieldValue) clearCell()
                setEditing(false)
            }
        }
    }, [editing, column, fieldValue, rowIdx, onUpdate])

    const commitEdit = useCallback(() => {
        setEditing(false)
        let committedValue = editValue
        if (column.type === 'number' || column.type === 'amount') {
            committedValue = emptyNumber(committedValue) ? undefined : parseFloat(committedValue)
            if (Number.isNaN(committedValue)) committedValue = undefined
        }
        if (committedValue !== fieldValue && onUpdate) {
            onUpdate(rowIdx, column.id, committedValue)
        }
        onCellEditingChange?.(rowIdx, colIdx, false)
    }, [editValue, column, rowIdx, fieldValue, onUpdate, onCellEditingChange, colIdx])

    /*
     * Empties the cell, and for a picker every field its choice filled in, so
     * a line typed as a purchase does not keep the purchase's name or side.
     * Undefined rather than null: the field leaves the record, and the
     * schema's own default (or nothing) applies.
     */
    const clearCell = () => {
        const keys = column.valueMap ? Object.keys(column.valueMap) : [column.id]
        onUpdate?.(rowIdx, Object.fromEntries(keys.map(k => [k, undefined])))
    }
    const isPicker = column.type === 'select' || column.type === 'connect' || column.type === 'hconnect'

    const handleKeyDown = (e) => {
        // Delete on a cell clears it, as in a spreadsheet.
        if (!editing && isEditable && (e.key === 'Delete' || e.key === 'Backspace')) {
            e.preventDefault()
            clearCell()
            return
        }
        // A picker whose text was emptied is cleared when it is left.
        if (editing && isPicker && (e.key === 'Tab' || e.key === 'Enter') && e.target.value === '') {
            clearCell()
            if (e.key === 'Enter') {
                e.preventDefault()
                setEditing(false)
                onCellEditingChange?.(rowIdx, colIdx, false)
                return
            }
        }
        if (e.key === 'Enter') {
            e.preventDefault()
            if (editing) {
                // A picker commits its own choice on Enter; closing the cell
                // here would unmount it before the choice lands.
                if (column.type !== 'connect' && column.type !== 'hconnect' && column.type !== 'select') {
                    commitEdit()
                }
            } else if (isEditable) {
                const isConnect = column.type === 'connect' || column.type === 'hconnect'
                autoOpenRef.current = isConnect
                onCellClick?.(rowIdx, colIdx)
                startEdit()
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
            setEditValue(null)
            onCellEditingChange?.(rowIdx, colIdx, false)
        } else if (e.key === 'Tab') {
            e.preventDefault()
            if (column.type !== 'connect' && column.type !== 'hconnect' && column.type !== 'select') {
                commitEdit()
            } else {
                setEditing(false)
            }
            onNavigate?.(e.shiftKey ? 'prev' : 'next', rowIdx, colIdx, e.ctrlKey)
        } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && (e.ctrlKey || !editing)) {
            // Ctrl moves and opens where it lands (entry); a plain arrow on a
            // closed cell roams (browse) and opens nothing.
            e.preventDefault()
            const isConnect = column.type === 'connect' || column.type === 'hconnect' || column.type === 'select'
            if (editing) { isConnect ? setEditing(false) : commitEdit() }
            onNavigate?.(e.key === 'ArrowUp' ? 'up' : 'down', rowIdx, colIdx, e.ctrlKey, e.shiftKey, !e.ctrlKey)
        } else if (e.key === 'ArrowRight' && (e.ctrlKey || !editing)) {
            e.preventDefault()
            const isConnect = column.type === 'connect' || column.type === 'hconnect' || column.type === 'select'
            if (editing) { isConnect ? setEditing(false) : commitEdit() }
            onNavigate?.('next', rowIdx, colIdx, e.ctrlKey, false, !e.ctrlKey)
        } else if (e.key === 'ArrowLeft' && (e.ctrlKey || !editing)) {
            e.preventDefault()
            const isConnect = column.type === 'connect' || column.type === 'hconnect' || column.type === 'select'
            if (editing) { isConnect ? setEditing(false) : commitEdit() }
            onNavigate?.('prev', rowIdx, colIdx, e.ctrlKey, false, !e.ctrlKey)
        }
    }

    const handleChange = (e) => {
        setEditValue(e.target.value)
    }

    const handleSelectChange = (value) => {
        setEditValue(value)
        if (onUpdate) {
            onUpdate(rowIdx, column.id, value)
        }
        setEditing(false)
        onCellEditingChange?.(rowIdx, colIdx, false)
    }

    const renderDisplayValue = () => {
        const displayType = column.type === 'amount' ? 'amount'
            : (column.type === 'connect' || column.type === 'hconnect') ? 'text'
            : column.type
        const value = displayField || fieldValue
        const isConnect = column.type === 'connect' || column.type === 'hconnect'
        return (
            <>
                <p style={{ flex: 1, minWidth: 0, margin: 0 }}>
                    <DisplayElement
                        string={value}
                        type={displayType}
                        item={row}
                        col={column}
                    />
                    {column.decorator && row[column.decorator] && (
                        <TableDecorator>{row[column.decorator]}</TableDecorator>
                    )}
                </p>
                {isEditable && isConnect && (
                    <span style={{ flexShrink: 0, opacity: 0.35, display: 'flex', alignItems: 'center', paddingRight: '2px' }}>
                        {Icon_Search}
                    </span>
                )}
            </>
        )
    }

    const renderEditInput = () => {
        switch (column.type) {
            case 'select': {
                const selectSetValue = (events) => {
                    if (!Array.isArray(events)) return
                    const updates = {}
                    // A null is only ever sent when the column's nullDefaults ask
                    // for it — a pick that clears what it no longer applies to.
                    events.forEach(({ target: { name, value } }) => {
                        if (value !== undefined) updates[name] = value
                    })
                    onUpdate?.(rowIdx, updates)
                    setEditing(false)
                    onCellEditingChange?.(rowIdx, colIdx, false)
                }
                return (
                    <Select
                        entity={column.entity || entity}
                        valueName={row[column.displayField] || ''}
                        valueId={fieldValue || ''}
                        valueMap={column.valueMap}
                        nullDefaults={column.nullDefaults}
                        entityParams={column.entityParams}
                        linkedFields={row}
                        options={column.options || { autoSelectFirst: false }}
                        setValue={selectSetValue}
                        // An empty cell shows empty — never the list's default,
                        // which the grid does not apply.
                        defaultType="none"
                        onAdvance={() => onNavigate?.('next', rowIdx, colIdx)}
                        autoFocus
                    />
                )
            }
            case 'number':
            case 'amount':
                return (
                    <NumericInput
                        name={column.id}
                        // An empty cell opens empty: Number('') would show it as 0.
                        value={editValue !== null && editValue !== undefined && editValue !== '' ? Number(editValue) : undefined}
                        $variant={column.type === 'amount' ? 'amount' : undefined}
                        decimals={column.decimals ?? 0}
                        setValue={handleNumericSetValue}
                        allowNegative={column.allowNegative !== false}
                        onBlur={() => setEditing(false)}
                        autoFocus
                    />
                )
            case 'date':
                return (
                    <DateInput
                        item={column}
                        fieldValue={editValue}
                        onChange={(val) => {
                            setEditValue(val)
                            onUpdate?.(rowIdx, column.id, val)
                            setEditing(false)
                        }}
                    />
                )
            case 'connect': {
                const connectSetValue = (events) => {
                    if (!Array.isArray(events)) return
                    const updates = {}
                    events.forEach(({ target: { name, value } }) => {
                        if (value !== undefined) updates[name] = value
                    })
                    onUpdate?.(rowIdx, updates)
                    setEditing(false)
                    onNavigate?.('next', rowIdx, colIdx)
                }
                return (
                    <Connect
                        entity={column.entity || entity}
                        mainValueId={editValue}
                        valueName={displayField || fieldValue}
                        valueMap={column.valueMap}
                        nullDefaults={column.nullDefaults}
                        // `${field}` in a filter reads the row: a document
                        // column searches the documents of the row's party.
                        entityParams={column.entityParams}
                        linkedFields={row}
                        menuItemField={column.menuItemField || 'name'}
                        displayField={column.displayField}
                        listDisplay={column.listDisplay}
                        modalTitle={column.modalTitle}
                        options={column.options || {}}
                        setValue={connectSetValue}
                        $width="100%"
                        $height="100%"
                        $marginBottom={0}
                        autoOpen={autoOpenRef.current}
                        inline={column.inline || false}
                        autoFocus={column.inline || false}
                    />
                )
            }
            case 'hconnect': {
                const hconnectSetValue = (events) => {
                    if (!Array.isArray(events)) return
                    const updates = {}
                    events.forEach(({ target: { name, value } }) => {
                        if (value !== null && value !== undefined) updates[name] = value
                    })
                    onUpdate?.(rowIdx, updates)
                    setEditing(false)
                    onNavigate?.('next', rowIdx, colIdx)
                }
                return (
                    <HConnect
                        entity={column.entity || entity}
                        mainValueId={editValue}
                        valueName={displayField || fieldValue}
                        valueMap={column.valueMap}
                        menuItemField={column.menuItemField || 'name'}
                        options={column.options || {}}
                        setValue={hconnectSetValue}
                        autoOpen={autoOpenRef.current}
                    />
                )
            }
            case 'boolean':
                return (
                    <input
                        ref={inputRef}
                        type="checkbox"
                        checked={!!editValue}
                        onChange={(e) => {
                            setEditValue(e.target.checked)
                            onUpdate?.(rowIdx, column.id, e.target.checked)
                            setEditing(false)
                        }}
                    />
                )
            case 'text':
            default:
                return (
                    <input
                        ref={inputRef}
                        type="text"
                        value={editValue ?? ''}
                        onChange={handleChange}
                        onBlur={commitEdit}
                        onKeyDown={handleKeyDown}
                    />
                )
        }
    }

    return (
        <CellWrapper
            ref={wrapperRef}
            $len={column.width}
            $editable={isEditable}
            $focused={isFocused}
            $editing={editing}
            tabIndex={isEditable ? 0 : undefined}
            onFocus={handleFocus}
            onBlur={handleCellBlur}
            onKeyDown={handleKeyDown}
        >
            {editing ? (
                <EditInput>
                    {renderEditInput()}
                </EditInput>
            ) : (
                <ClickableDisplay $editable={isEditable} $hovered={hoverActive} onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    {renderDisplayValue()}
                </ClickableDisplay>
            )}
        </CellWrapper>
    )
}

export default GridListCell
